import { Stack, Typography } from '@mui/material';
import { InformationRow } from '../../design-system';
import type { AbsenceConditionPolicy, AbsenceRequestableTimeOffType, AbsenceTimeOffRequestCalculation } from './model';

export function absenceEvidenceTiming(type: Pick<AbsenceRequestableTimeOffType, 'evidencePolicy'>, evidenceRequired = false): string {
  if (evidenceRequired || type.evidencePolicy === 'BEFORE_REQUEST') return '신청 전 제출 필수';
  if (type.evidencePolicy === 'AFTER_USE') return '사용 후 제출 필수 · 신청할 때도 첨부 가능';
  return '제출 불필요';
}

export function AbsenceRequestPolicySummary({ type, policy, calculation }: {
  type?: AbsenceRequestableTimeOffType;
  policy: AbsenceConditionPolicy;
  calculation?: AbsenceTimeOffRequestCalculation;
}) {
  if (!type) return null;
  const limits = calculation?.usageLimits ?? (type.usageLimit ? [type.usageLimit] : []);
  const items: [string, string][] = [];
  if (type.managementMode === 'USAGE_LIMIT') {
    items.push(['사용 한도', limits.length ? limits.map((limit) => {
      const period = limit.periodStart.endsWith('-01-01') && limit.periodEnd.endsWith('-12-31') ? '연간' : limit.periodStart.slice(0, 7) === limit.periodEnd.slice(0, 7) ? '월간' : `${limit.periodStart} ~ ${limit.periodEnd}`;
      return `${period} ${Number(limit.limitQuantity).toLocaleString('ko-KR')}일`;
    }).join(' · ') : '사용 날짜를 선택하면 확인할 수 있습니다.']);
  }
  if (policy.requestCondition === 'UNPAID') {
    items.push(['신청 기준', `${policy.prerequisiteTimeOffType?.descriptor || '연차'} 소진 · 승인 대기를 반영한 신청 가능 잔액 0일`]);
    items.push(['신청 사유', '입사 1년 미만 또는 불가피한 개인 사유']);
  } else if (policy.requestCondition === 'HOSPITALIZATION') {
    items.push(['신청 기준', `${policy.minimumHospitalizationDays ?? 4}일 이상 입원 · 입원일과 퇴원일 포함`]);
    items.push(['사용 범위', '입원 기간 내 근무일']);
  }
  if (type.allowedUnit === 'DAYS') {
    items.push(['일수 계산', type.dayCountBasis === 'CALENDAR_DAYS' ? '주말·공휴일 포함' : '주말·공휴일 제외']);
  }
  if (type.reasonRequired) items.push(['상세 사유', '선택한 휴가의 구체적인 신청 사유를 적어 주세요.']);
  if (type.maximumConsecutiveDays) items.push(['연속 사용', `한 번에 최대 ${type.maximumConsecutiveDays}일 · 주말 포함`]);
  items.push(['증빙 제출', absenceEvidenceTiming(type, calculation?.evidenceRequired)]);
  return <Stack spacing={1}>
    <Typography variant="subtitle2">휴가 정책</Typography>
    <Stack component="dl" spacing={1.5} sx={{ m: 0 }}>{items.map(([label, value]) => <InformationRow key={label} label={label} value={value} layout="stacked" />)}</Stack>
    {policy.requestCondition === 'UNPAID' && <Typography variant="caption" color="text.secondary">무급휴가는 연차와 별도로 신청하며, 연차에서 자동으로 전환되거나 차감되지 않습니다.</Typography>}
    {policy.requestCondition === 'HOSPITALIZATION' && <Typography variant="caption" color="text.secondary">입원 기준 확인에 필요한 기간만 입력해 주세요. 진단명 등 상세 건강 정보는 메모에 입력하지 않아도 됩니다.</Typography>}
    {type.evidencePolicy === 'AFTER_USE' && <Typography variant="caption" color="text.secondary">사용 후 신청 상세의 ‘증빙 추가’에서 제출할 수 있습니다.</Typography>}
    <Typography variant="caption" color="text.secondary">선택한 유형과 사용 시작일에 적용되는 정책입니다. 실제 증빙 내용과 회사의 사전·사후 제출 절차는 담당자에게 확인해 주세요.</Typography>
  </Stack>;
}
